import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Building2, Users, Clock, Shield, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import ServiceFAQ from "@/components/ServiceFAQ";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import corporateTravelImg from "@/assets/corporate-travel.webp";
const CorporateTravel = () => {
  // Corporate Car Service FAQs - Optimized for high-intent keywords
  const faqs = [
    {
      question: "How much does Corporate Car Service in San Antonio cost?",
      answer: "Our Corporate Car Service in San Antonio offers transparent, flat-rate pricing for point-to-point transfers and hourly rates starting at $100/hour with a 3-hour minimum for as-directed service. We provide detailed quotes upfront with no hidden fees—perfect for executive travel budgets and expense reporting."
    },
    {
      question: "Is your Executive Transportation Service available 24/7?",
      answer: "Yes! Our Executive Transportation Service operates 24/7, 365 days a year. Whether you need early morning SAT airport pickup for a 6 AM flight or late-night transportation after a client dinner, our professional chauffeurs are always available to serve San Antonio's business community."
    },
    {
      question: "Do you offer Corporate Account options for businesses?",
      answer: "Absolutely. Our Corporate Car Service in San Antonio offers dedicated Corporate Account options with simplified billing, priority booking, consistent chauffeur assignments, and detailed trip reports. Contact us to discuss tailored solutions for your company's Executive Transportation Service needs."
    },
    {
      question: "What vehicle do you use for Corporate Transportation?",
      answer: "We exclusively use a 2025 Audi Q7 Black Optic for all Corporate Car Service in San Antonio. This luxury SUV provides Executive-class comfort with leather seating, climate control, and a quiet environment ideal for business calls and meeting preparation during your transportation."
    },
    {
      question: "Can you accommodate last-minute business travel requests?",
      answer: "Yes! Our Executive Transportation Service specializes in accommodating last-minute corporate travel needs. With 24/7 availability, we can often arrange same-day Corporate Car Service in San Antonio. Call (210) 296-7409 for immediate booking assistance."
    }
  ];
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/services/corporate-travel#service",
    "name": "Corporate Car Service San Antonio",
    "serviceType": "Corporate Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "url": "https://ricksblackcarservicesantonio.com"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "addressRegion": "Texas"
    },
    "description": "Professional corporate transportation in San Antonio for business meetings, conferences, and executive travel. Luxury Audi Q7 with experienced chauffeur. Hourly service and point-to-point available 24/7. Ideal for executives, business travelers, and corporate accounts.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "category": "Corporate Transportation Service",
    "serviceOutput": "Professional executive transportation with reliable, discreet chauffeur service",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Corporate Transportation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Hourly Executive Service",
          "description": "Professional SAT airport pickup and drop-off with flight tracking and meet & greet service",
          "price": "135",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Hourly Executive Service"
          }
        },
        {
          "@type": "Offer",
          "name": "Half-Day Corporate Package",
          "description": "4-hour executive transportation for multiple business meetings throughout San Antonio",
          "price": "540",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Half-Day Corporate Package (4 hours)"
          }
        },
        {
          "@type": "Offer",
          "name": "Full-Day Corporate Package",
          "description": "8-hour executive transportation for roadshows and extended business trips",
          "price": "1080",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Full-Day Corporate Package (8 hours)"
          }
        }
      ]
    },
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/services/corporate-travel",
      "priceCurrency": "USD",
      "lowPrice": "135",
      "highPrice": "1080",
      "offerCount": "3",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "validFrom": "2024-01-01",
      "seller": {
        "@type": "LocalBusiness",
        "name": "Rick's Black Car Service",
        "telephone": "+1-210-296-7409"
      }
    },
    "priceRange": "$135/hour",
    "additionalType": "https://schema.org/TaxiService",
    "audience": {
      "@type": "Audience",
      "audienceType": "Business Executives, Corporate Travelers, Business Professionals"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+1-210-296-7409",
        "contactType": "Corporate Reservations"
      },
      "serviceUrl": "https://ricksblackcarservicesantonio.com/services/corporate-travel"
    }
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <>
      <SEO title="Corporate Car Service San Antonio | Rick's Black Car Service" description="San Antonio's premier executive car service. Reliable, discreet transportation for corporate travel to The Pearl, La Cantera & downtown." keywords="executive car service san antonio, san antonio corporate car service, private car services san antonio, corporate chauffeur service, san antonio business transportation" canonicalPath="/services/corporate-travel" schema={[schemaMarkup, faqSchema]} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "Corporate Travel", url: "/services/corporate-travel" }
            ]}
          />

          {/* Hero Section */}
          <section className="relative h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <img src={corporateTravelImg} alt="Corporate transportation San Antonio - executive car service" className="w-full h-full object-cover brightness-75" />
              <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/70" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground mb-6">
                San Antonio Corporate Car Service: Executive &amp; Private Chauffeur
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Executive Transportation for Business Professionals | Available 24/7
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Your Private Executive Car Service Today
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Experience premier <strong className="text-foreground">Executive Car Service San Antonio</strong> with Rick's Black Car Service. As your trusted <strong className="text-foreground">San Antonio Corporate Car Service</strong> provider, we deliver premium corporate transportation solutions for San Antonio's business community. Our professional chauffeur service provides the reliability, discretion, and sophistication that executives and companies demand.
                  </p>
                  
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6 mt-8">
                    Reliable Executive Car Service for Meetings and Corporate Events
                  </h2>
                  <ul className="space-y-3 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Luxury SUV:</strong> Executive comfort with ample luggage space</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">On-Time, Discreet Chauffeurs:</strong> Vetted for professionalism</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Hourly or Point-to-Point:</strong> Transparent rates</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Reliability:</strong> Punctuality is everything in business</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Productive Travel Time:</strong> Quiet environment for calls and prep</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Discreet Service:</strong> Confidentiality and privacy paramount</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Consistent Service:</strong> Same professional chauffeur every time</span>
                    </li>
                  </ul>
                </div>

                {/* CTA Card */}
                <Card className="card-luxury my-12 border-2 border-primary/20">
                  <CardContent className="p-10 text-center">
                    <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                      Book Your Private Executive Car Service Today
                    </h2>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="tel:+12102967409">
                          📞 Call Now: 210-296-7409
                        </a>
                      </Button>
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                          Book Your Private Executive Car Service Today
                        </a>
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-lg">
                      Available 24/7 | Professional Chauffeurs | Luxury Fleet
                    </p>
                  </CardContent>
                </Card>

                {/* Corporate Services */}
                <div className="my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8">
                    Corporate Services
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="card-luxury">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">SAT Airport Transfers for Business Travelers</h3>
                        <p className="text-muted-foreground">Reliable SAT airport transportation with flight tracking and meet & greet service</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">Business Meeting Transportation</h3>
                        <p className="text-muted-foreground">Professional service for client meetings and business appointments</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">Client Entertainment and Hospitality</h3>
                        <p className="text-muted-foreground">Impress clients with luxury transportation experiences</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">Corporate Events and Conferences</h3>
                        <p className="text-muted-foreground">Group transportation coordination for corporate gatherings</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">Hourly and As-Directed Service</h3>
                        <p className="text-muted-foreground">Flexible transportation for full-day business itineraries</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">Road Shows and Multi-City Travel</h3>
                        <p className="text-muted-foreground">Extended executive transportation across multiple locations</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Business Hubs Section */}
                <div className="my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Serving San Antonio's Business Hubs
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Our 2025 Audi Q7 Black Optic is the perfect vehicle for visiting executives who require discretion and punctuality. With its refined interior, whisper-quiet cabin, and ample space for luggage and presentation materials, you arrive at every destination composed and ready for business.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="card-luxury border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">The Pearl District</h3>
                        <p className="text-muted-foreground">Premier destination for client dinners, creative meetings, and upscale hospitality. We provide seamless drop-off and pickup at Hotel Emma, restaurants, and event venues.</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">La Cantera</h3>
                        <p className="text-muted-foreground">Reliable transportation to corporate offices, retail headquarters, and the La Cantera Resort & Spa for executive retreats and client entertainment.</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">The Rim</h3>
                        <p className="text-muted-foreground">Efficient access to the growing business corridor along I-10 West, including tech offices, corporate campuses, and professional services firms.</p>
                      </CardContent>
                    </Card>
                    <Card className="card-luxury border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-semibold text-foreground mb-2">Downtown Convention Center</h3>
                        <p className="text-muted-foreground">Punctual arrivals for conferences, trade shows, and corporate events at the Henry B. González Convention Center. Perfect for out-of-town executives attending major gatherings.</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Service Features */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12">
                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Building2 className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Corporate Account Options</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Simplified billing, priority booking, consistent service, detailed trip reports. Contact us to discuss corporate solutions tailored to your company's needs.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-luxury">
                    <CardContent className="p-8">
                      <div className="flex items-start gap-4">
                        <Users className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">Industries We Serve</h3>
                          <p className="text-muted-foreground leading-relaxed">
                            Healthcare, Technology, Finance, Legal, Real Estate, Energy, Hospitality, Government, and more. Trusted by San Antonio's leading professionals.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Additional Content */}
                <div className="prose prose-lg max-w-none my-12">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Call (210) 296-7409 to elevate your business travel experience.
                  </p>
                </div>

                {/* Service Areas */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h3 className="text-2xl font-serif font-bold text-foreground mb-6 text-center">
                      Corporate Service Areas
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Downtown Business District</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Medical Center</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Stone Oak Corporate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>La Cantera</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>The Dominion</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>SAT Airport</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Convention Center</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Pearl District</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span>Austin</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* FAQ Section */}
                <ServiceFAQ faqs={faqs} />

                {/* Final CTA */}
                <div className="text-center my-12">
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                    Elevate Your Corporate Travel Experience
                  </h2>
                  <p className="text-muted-foreground text-lg mb-8">
                    Join the leading companies in San Antonio who trust Rick's for their executive transportation needs. Contact us today to set up your corporate account.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-6 h-6 mr-2" />
                        Call: 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                        Get Instant Quote <ArrowRight className="w-6 h-6 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default CorporateTravel;